<script lang="ts">
	import { cfgToThemeCdnPath } from '@infinitro/config';
	import { Verbiage, t } from '@infinitro/content';
	import { isAndroid, registerActiveForm } from '@infinitro/global';
	import { CloseIcon, SupportChatSendIcon } from '@infinitro/icons';
	import { MediaPicker } from '@infinitro/media-picker';
	import { onDestroy } from 'svelte';
	import { supportChatMessageCharacterLength } from '../api';
	import { autosizeSupportChatTextarea } from '../utils/support-chat-textarea';

	/** Theme CDN assets: `/support-chat/*.svg` */
	const composerActionIconFile = cfgToThemeCdnPath('/support-chat/file.svg');
	const composerActionIconEmoji = cfgToThemeCdnPath('/support-chat/emoji.svg');
	const composerActionIconGif = cfgToThemeCdnPath('/support-chat/gif.svg');

	const composerAriaAttachFile = 'Attach file';
	const composerFileInputAccept = 'image/png,image/jpeg,image/jpg,image/webp,image/gif';

	/** Stable id for label/input pairing (single instance in widget today). */
	export let composerInputId = 'support-chat-messages-composer';

	export let question = '';
	/** Selected GIF URL from Giphy (same source as `@infinitro/chat`). */
	export let messageGifUrl = '';
	/** Local image file pending upload via the attachment presign flow. */
	export let selectedImageFile: File | null = null;
	export let inputElement: HTMLTextAreaElement | null = null;
	export let isSendingMessage = false;
	export let contentTooLong = false;
	export let maxContentCharacters = Infinity;
	export let onInputKeydown: (event: KeyboardEvent) => void = () => {};
	export let onSubmitQuestion: () => void = () => {};

	let fileInputElement: HTMLInputElement | null = null;
	let selectedImagePreviewUrl = '';
	let selectedImageName = '';
	let showMediaPicker = false;
	let mediaPickerInitialTab = 0;
	let mediaPickerSessionKey = 0;

	function revokeSelectedPreview() {
		if (!selectedImagePreviewUrl) return;
		URL.revokeObjectURL(selectedImagePreviewUrl);
		selectedImagePreviewUrl = '';
	}

	function clearSelectedImage() {
		revokeSelectedPreview();
		selectedImageName = '';
		selectedImageFile = null;
		if (fileInputElement) {
			fileInputElement.value = '';
		}
	}

	function clearSelectedGif() {
		messageGifUrl = '';
	}

	function openFilePicker() {
		showMediaPicker = false;
		fileInputElement?.click();
	}

	function onFileChange(event: Event) {
		const target = event.currentTarget as HTMLInputElement | null;
		const selectedFile = target?.files?.[0];

		if (!selectedFile || !selectedFile.type.startsWith('image/')) {
			clearSelectedImage();
			return;
		}

		clearSelectedGif();
		showMediaPicker = false;
		revokeSelectedPreview();
		selectedImagePreviewUrl = URL.createObjectURL(selectedFile);
		selectedImageName = selectedFile.name;
		selectedImageFile = selectedFile;
	}

	function openMediaPicker(tab: 0 | 1) {
		clearSelectedImage();
		if (showMediaPicker && mediaPickerInitialTab === tab) {
			showMediaPicker = false;
			return;
		}
		mediaPickerInitialTab = tab;
		mediaPickerSessionKey += 1;
		showMediaPicker = true;
	}

	function addEmoji(emoji: string) {
		question += emoji;
		showMediaPicker = false;
		inputElement?.focus();
	}

	function selectGif(gifUrl: string) {
		messageGifUrl = gifUrl;
		clearSelectedImage();
		showMediaPicker = false;
	}

	$: hasComposerContent = Boolean(question.trim() || messageGifUrl.trim() || selectedImageFile);
	$: canSubmit = hasComposerContent && !contentTooLong;

	$: contentLength = supportChatMessageCharacterLength(question);
	$: void autosizeSupportChatTextarea(inputElement, question);

	// Reports unsaved composer content to `safeToReload` so the update popup doesn't interrupt a draft.
	let releaseActiveForm: (() => void) | null = null;
	$: if (hasComposerContent && !releaseActiveForm) {
		releaseActiveForm = registerActiveForm();
	} else if (!hasComposerContent && releaseActiveForm) {
		releaseActiveForm();
		releaseActiveForm = null;
	}

	// selectedImageFile is bound two-way, so a parent reset (e.g. after a successful send) sets it
	// to null here too — but the preview thumbnail lives in local state and needs to be told to clear.
	$: if (!selectedImageFile) {
		revokeSelectedPreview();
		selectedImageName = '';
		if (fileInputElement) fileInputElement.value = '';
	}

	onDestroy(() => {
		revokeSelectedPreview();
		releaseActiveForm?.();
	});
</script>

<div class="message-composer" class:message-composer-android={$isAndroid}>
	{#if showMediaPicker}
		<div class="message-composer-media-picker">
			{#key mediaPickerSessionKey}
				<MediaPicker
					variant="light"
					enableStickersTab={false}
					initialTab={mediaPickerInitialTab}
					on:emoji-selected={(event) => addEmoji(event.detail)}
					on:gif-selected={(event) => selectGif(event.detail)}
				/>
			{/key}
		</div>
	{/if}

	<div class="message-composer-card">
		{#if selectedImagePreviewUrl}
			<div class="message-composer-attachment-preview">
				<img
					class="message-composer-attachment-image"
					src={selectedImagePreviewUrl}
					alt={selectedImageName}
				/>
				<button
					class="message-composer-attachment-remove"
					type="button"
					aria-label={$t(Verbiage.delete)}
					on:click={clearSelectedImage}
				>
					<span class="message-composer-attachment-remove-icon" aria-hidden="true">
						<CloseIcon />
					</span>
				</button>
			</div>
		{/if}

		{#if messageGifUrl}
			<div class="message-composer-attachment-preview message-composer-gif-preview">
				<img
					class="message-composer-attachment-image"
					src={messageGifUrl}
					alt={$t(Verbiage.selected_gif_alt, 'alts')}
				/>
				<button
					class="message-composer-attachment-remove"
					type="button"
					aria-label={$t(Verbiage.delete)}
					on:click={clearSelectedGif}
				>
					<span class="message-composer-attachment-remove-icon" aria-hidden="true">
						<CloseIcon />
					</span>
				</button>
			</div>
		{/if}

		<label class="sr-only" for={composerInputId}>
			{$t(Verbiage.message)}
		</label>
		<textarea
			id={composerInputId}
			class="message-composer-input"
			rows="1"
			autocomplete="new-password"
			autocapitalize="sentences"
			enterkeyhint="send"
			inputmode="text"
			spellcheck="true"
			bind:value={question}
			bind:this={inputElement}
			placeholder={`${$t(Verbiage.message)}...`}
			on:keydown={onInputKeydown}
		></textarea>
		{#if contentTooLong}
			<p class="message-composer-error" role="alert">
				{$t(Verbiage.support_chat_message_too_long_error)}
			</p>
		{:else if contentLength > 0}
			<p
				class="message-composer-counter"
				class:message-composer-counter-limit={contentLength >= maxContentCharacters}
			>
				{contentLength}/{maxContentCharacters}
			</p>
		{/if}
		<div class="message-composer-toolbar">
			<div class="message-composer-actions">
				<input
					class="message-composer-file-input"
					type="file"
					accept={composerFileInputAccept}
					bind:this={fileInputElement}
					on:change={onFileChange}
				/>
				<button
					class="message-composer-action"
					type="button"
					aria-label={composerAriaAttachFile}
					on:click={openFilePicker}
				>
					<img
						class="message-composer-action-icon"
						src={composerActionIconFile}
						alt=""
						draggable="false"
					/>
				</button>
				<button
					class="message-composer-action"
					type="button"
					aria-label={$t(Verbiage.chat_media_emojis_tab)}
					aria-pressed={showMediaPicker && mediaPickerInitialTab === 0}
					on:click={() => openMediaPicker(0)}
				>
					<img
						class="message-composer-action-icon"
						src={composerActionIconEmoji}
						alt=""
						draggable="false"
					/>
				</button>
				<button
					class="message-composer-action"
					type="button"
					aria-label={$t(Verbiage.chat_media_gifs_tab)}
					aria-pressed={showMediaPicker && mediaPickerInitialTab === 1}
					on:click={() => openMediaPicker(1)}
				>
					<img
						class="message-composer-action-icon"
						src={composerActionIconGif}
						alt=""
						draggable="false"
					/>
				</button>
			</div>
			<button
				class="message-composer-send"
				type="button"
				disabled={!canSubmit || isSendingMessage}
				aria-label={$t(Verbiage.submit)}
				on:click={onSubmitQuestion}
			>
				<span class="message-composer-send-icon">
					<SupportChatSendIcon size={20} />
				</span>
			</button>
		</div>
	</div>
</div>

<style>
	.message-composer {
		flex-shrink: 0;
		border-width: 0px;
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-bottom: 0.75rem;
		padding-top: 0.5rem;
}

	.message-composer-android:focus-within {
		padding-bottom: calc(3rem + env(safe-area-inset-bottom, 0px));
	}

	.message-composer-media-picker {
		margin-bottom: 0.5rem;
		min-width: 0px;
		overflow: hidden;
}

	.message-composer-card {
		display: flex;
		min-width: 0px;
		flex-direction: column;
		gap: 0.5rem;
		border-radius: 18px;
		border-width: 1px;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		border-color: color-mix(in srgb, var(--color-text-900) 7%, var(--color-text-100));
		box-shadow:
			0 1px 2px rgba(var(--color-background-900-rgb), 0.04),
			0 4px 14px rgba(var(--color-background-900-rgb), 0.06);
}

	.message-composer-input {
		margin: 0px;
		display: block;
		max-height: 144px;
		min-height: 40px;
		width: 100%;
		resize: none;
		overflow-y: auto;
		border-radius: 0px;
		border-width: 0px;
		background-color: transparent;
		padding: 0px;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-900);
		-webkit-appearance: none;
		   -moz-appearance: none;
		        appearance: none;
		box-shadow: none;
		outline: none;
		white-space: pre-wrap;
		word-break: break-word;
}

	.message-composer-input:focus,
	.message-composer-input:focus-visible {
		outline: none;
		box-shadow: none;
	}

	.message-composer-input::-moz-placeholder {
		color: rgba(var(--color-text-900-rgb), 0.38);
	}

	.message-composer-input::placeholder {
		color: rgba(var(--color-text-900-rgb), 0.38);
	}

	.message-composer-error {
		margin: 0px;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-error-500);
}

	.message-composer-counter {
		margin: 0px;
		text-align: right;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-500);
}

	.message-composer-counter-limit {
		color: var(--color-error-500);
	}

	.message-composer-attachment-preview {
		position: relative;
		height: 4rem;
		width: 4rem;
		overflow: hidden;
		border-radius: 12px;
		border: 1px solid color-mix(in srgb, var(--color-text-900) 10%, var(--color-text-100));
}

	.message-composer-gif-preview {
		height: 5rem;
		width: 5rem;
}

	.message-composer-attachment-image {
		height: 100%;
		width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.message-composer-attachment-remove {
		position: absolute;
		right: 0.125rem;
		top: 0.125rem;
		display: flex;
		height: 1.25rem;
		width: 1.25rem;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		border-width: 0px;
		padding: 0px;
		background-color: var(--color-background-900);
		color: var(--color-text-100);
}

	.message-composer-attachment-remove:hover {
		background-color: rgba(var(--color-background-900-rgb), 1);
	}

	.message-composer-attachment-remove:focus-visible {
		outline-style: solid;
		outline-width: 2px;
		outline-offset: 1px;
		outline-color: var(--color-secondary-500);
}

	.message-composer-attachment-remove-icon {
		display: block;
		height: 0.75rem;
		width: 0.75rem;
}

	.message-composer-toolbar {
		display: flex;
		min-width: 0px;
		align-items: flex-end;
		justify-content: space-between;
		gap: 0.75rem;
		border-width: 0px;
}

	.message-composer-file-input {
		display: none;
}

	.message-composer-actions {
		display: flex;
		min-width: 0px;
		flex: 1 1 0%;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.25rem;
}

	.message-composer-action {
		display: flex;
		height: 2rem;
		width: 2rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		border-width: 0px;
		background-color: transparent;
		padding: 0px;
		color: var(--color-text-500);
}

	.message-composer-action:hover {
		background-color: rgba(var(--color-background-900-rgb), 0.06);
	}

	.message-composer-action[aria-pressed='true'] {
		background-color: color-mix(in srgb, var(--color-secondary-500) 14%, #ffffff);
	}

	.message-composer-action[aria-pressed='true'] .message-composer-action-icon {
		opacity: 1;
	}

	.message-composer-action:focus-visible {
		outline-style: solid;
		outline-width: 2px;
		outline-offset: 2px;
		outline-color: var(--color-secondary-500);
}

	.message-composer-action-icon {
		height: 1rem;
		width: 1rem;
		-webkit-user-select: none;
		   -moz-user-select: none;
		        user-select: none;
		-o-object-fit: contain;
		   object-fit: contain;
		opacity: 0.72;
}

	.message-composer-send {
		display: flex;
		height: 2.25rem;
		width: 2.25rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		color: var(--color-text-100);
		background-color: color-mix(in srgb, var(--color-secondary-500) 92%, var(--color-text-100));
		background-image: none;
		box-shadow: 0 1px 2px rgba(var(--color-background-900-rgb), 0.08);
}

	.message-composer-send:disabled {
		cursor: not-allowed;
		background-image: none;
		background-color: var(--color-secondary-100);
		box-shadow: none;
}

	.message-composer-send:disabled .message-composer-send-icon :global(.support-chat-send-icon) {
		background: var(--color-text-100);
		opacity: 0.85;
	}

	.message-composer-send:not(:disabled):hover {
		background-color: color-mix(in srgb, var(--color-secondary-300) 100%, var(--color-text-100));
		filter: brightness(0.98);
	}

	.message-composer-send:focus-visible:not(:disabled) {
		outline-style: solid;
		outline-width: 2px;
		outline-offset: 2px;
		outline-color: var(--color-secondary-500);
}

	.message-composer-send-icon {
		display: flex;
		height: 18px;
		width: 18px;
		align-items: center;
		justify-content: center;
}

	.message-composer-send:not(:disabled)
		.message-composer-send-icon
		:global(.support-chat-send-icon) {
		background: var(--color-text-100);
	}

	.message-composer-send-icon :global(svg),
	.message-composer-send-icon :global(img) {
		height: 100%;
		width: 100%;
}

	.message-composer-attachment-remove-icon :global(svg),
	.message-composer-attachment-remove-icon :global(img) {
		height: 100%;
		width: 100%;
}

	.sr-only {
		position: absolute;
		height: 1px;
		width: 1px;
		overflow: hidden;
		white-space: nowrap;
		padding: 0px;
		clip: rect(0, 0, 0, 0);
		clip-path: inset(50%);
		margin: -1px;
		border: 0;
}
</style>
